import {EventEmitter, Injectable, OnDestroy} from "@angular/core";
import {ChartData} from "chart.js/auto";
import {ChartDataset} from "chart.js";
import {ColorMapService} from "../../../common/colors/color-map.service";
import {TimeSeriesPerformanceStoreService} from "../time-series-performance-store.service";
import {Subscription} from "rxjs";
import {CategoryRevenueRepository} from "../../repositories/derived/category-revenue/category-revenue-repository";
import {LinkedDtoRepository} from "../../../common/repositories/linked-dto-repository";
import {FilterKeys} from "../../../common/utils/filter/enums/filter-keys";
import {CombinedOrderElementDto} from "../../models/core/combined-order-element-dto";

@Injectable()
export class RevenueBreakdownStore implements OnDestroy {
  subscription: Subscription = new Subscription()
  isRegistered: boolean = false

  bookingTypeBreakdownDataset!: ChartData<"doughnut">
  locationBreakdownDataset!: ChartData<"doughnut">
  compositionTypeBreakdownDataset!: ChartData<"doughnut">


  displayOptionChangedNotifier: EventEmitter<any> = new EventEmitter<any>()
  selectedDisplayOption: FilterKeys = FilterKeys.BookingType


  constructor(private colorMapService: ColorMapService,
              private categoryRevenueRepository: CategoryRevenueRepository,
              // Todo: use a generic type
              private linkedDtoRepository: LinkedDtoRepository<any>,
              private timeSeriesPerformanceStoreService: TimeSeriesPerformanceStoreService
  ) {
    this.register()
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe()
  }

  register() {
    if (this.isRegistered)
      return
    this.isRegistered = true
    this.subscription.add(this.timeSeriesPerformanceStoreService.updateNotifier.subscribe(() => {
        this.update()
      }
    ))
  }

  update() {
    // Todo set a state for loading process -> loadingProcess is overwritten by timeSeriesPerformanceStoreService
    this.clear()
    this.processPieChartDatasetOrSkip()
    this.displayOptionChangedNotifier.emit()
  }

  changeDisplayOption(option: FilterKeys) {
    this.selectedDisplayOption = option
    this.processPieChartDatasetOrSkip()
    this.displayOptionChangedNotifier.emit()
  }

  getPieChart() {
    switch (this.selectedDisplayOption) {
      case FilterKeys.BookingType:
        return this.bookingTypeBreakdownDataset
      case FilterKeys.LocationId:
        return this.locationBreakdownDataset
      case FilterKeys.CompositionTypeId:
        return this.compositionTypeBreakdownDataset
    }
    console.error("Invalid display option")
    return {datasets: [], labels: []}
  }

  processPieChartDatasetOrSkip() {
    switch (this.selectedDisplayOption) {
      case FilterKeys.BookingType:
        if (this.bookingTypeBreakdownDataset)
          return
        this.bookingTypeBreakdownDataset = this.processBreakdownData(this.selectedDisplayOption as keyof CombinedOrderElementDto, this.timeSeriesPerformanceStoreService.revenueFn)
        break
      case FilterKeys.LocationId:
        if (this.locationBreakdownDataset)
          return
        this.locationBreakdownDataset = this.processBreakdownData(this.selectedDisplayOption as keyof CombinedOrderElementDto, this.timeSeriesPerformanceStoreService.revenueFn)
        break
      case FilterKeys.CompositionTypeId:
        if (this.compositionTypeBreakdownDataset)
          return
        this.compositionTypeBreakdownDataset = this.processBreakdownData(this.selectedDisplayOption as keyof CombinedOrderElementDto, this.timeSeriesPerformanceStoreService.revenueFn)
        break
    }
  }

  clear() {
    // todo create better method to verify if data is already processed
    // @ts-ignore
    this.bookingTypeBreakdownDataset = undefined
    // @ts-ignore
    this.locationBreakdownDataset = undefined
    // @ts-ignore
    this.compositionTypeBreakdownDataset = undefined
  }


  processBreakdownData(categoryKey: keyof CombinedOrderElementDto, revenueFn: (combinedOrderElementDto: CombinedOrderElementDto) => number): ChartData<"doughnut"> {
    //verify categoryKey is valid keyof CombinedOrderElementDto
    if (!this.categoryRevenueRepository.categoryRevenue[categoryKey]) {
      console.error("Invalid categoryKey")
      return {datasets: [], labels: []}
    }

    let data: ChartData<"doughnut"> = {datasets: [], labels: []};
    let dataset: ChartDataset<"doughnut"> = {data: [], backgroundColor: []}
    let counter = 0

    for (let category of this.categoryRevenueRepository.categoryRevenue[categoryKey]) {
      let revenue = category.revenue
      let name = this.linkedDtoRepository.getName(category.id, categoryKey)
      let color = this.colorMapService.getColor(counter, this.categoryRevenueRepository.categoryRevenue[categoryKey].length)
      data.labels!.push(name)
      dataset.data!.push(revenue)
      // @ts-ignore
      dataset.backgroundColor!.push(color)

      counter++

    }
    data.datasets!.push(dataset)
    return data
  }
}
